import { type FC, type PropsWithChildren, useState } from 'react'
import { Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { type AccountSchedulingSettings, ApprovalMode, AutomaticInvitesMode, type UserSchedulingSettings } from '../../../../models/Scheduling'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { useGetAllUserShareSettingsQuery, useGetUserByIdQuery } from '../../../../store/user/user'
import { LabeledRadioCards, type RadioCardOption } from '../../../Scheduling/shared/ApprovalRulesEditor'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { useSchedulingEmailTemplates } from '../../../Scheduling/shared/useSchedulingEmailTemplates'
import { SettingsCard, SettingsRow, SettingsSection } from '../../../Setup/Scheduling/sections/SettingsSection'
import { type UpdateUserSettings } from '../SchedulingProfileTab'
import { ResetToAccountDefaults } from './ResetToAccountDefaults'

interface SectionProps {
  userSettings: UserSchedulingSettings
  accountSettings?: AccountSchedulingSettings
  isSaving?: boolean
  onUpdate: UpdateUserSettings
  onReset: () => void
}

const MINUTES_PER_DAY = 24 * 60
const REMINDER_DAY_OPTIONS = [1, 2, 3, 5, 7]
const STOP_REMINDER_OPTIONS = [2, 3, 4, 5, 6]
const DEFAULT_REMINDER_MINUTES = 3 * MINUTES_PER_DAY

const bookingApprovalOptions: RadioCardOption<ApprovalMode>[] = [
  { value: ApprovalMode.ALWAYS_REQUIRE, label: 'Always require', description: 'All bookings wait for advisor approval.' },
  { value: ApprovalMode.NEVER_REQUIRE, label: 'Never require', description: 'All bookings confirm automatically.' },
]

const rescheduleApprovalOptions: RadioCardOption<ApprovalMode>[] = [
  { value: ApprovalMode.ALWAYS_REQUIRE, label: 'Always require', description: 'All reschedules wait for advisor approval.' },
  { value: ApprovalMode.NEVER_REQUIRE, label: 'Never require', description: 'All reschedules confirm automatically.' },
]

const automaticInviteOptions: RadioCardOption<AutomaticInvitesMode>[] = [
  { value: AutomaticInvitesMode.OFF, label: 'Off', description: "Invites don't send automatically." },
  { value: AutomaticInvitesMode.ON, label: 'On', description: 'Invites send automatically.' },
]

const approvalValue = (mode?: ApprovalMode): ApprovalMode => (mode === ApprovalMode.NEVER_REQUIRE ? ApprovalMode.NEVER_REQUIRE : ApprovalMode.ALWAYS_REQUIRE)

const inviteValue = (mode?: AutomaticInvitesMode): AutomaticInvitesMode =>
  mode === AutomaticInvitesMode.ON ? AutomaticInvitesMode.ON : AutomaticInvitesMode.OFF

const SettingsGroup: FC<PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <div className='d-flex flex-column zocks-gap-0.75'>
    <h6 className='mb-0 zocks-text-gray-900'>{title}</h6>
    <SettingsCard>{children}</SettingsCard>
  </div>
)

interface EnforcedValueRowProps {
  title: string
  description: string
  option?: RadioCardOption<string>
}

const EnforcedValueRow: FC<EnforcedValueRowProps> = ({ title, description, option }) => (
  <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3 zocks-bg-color-gray-100'>
    <div className='d-flex flex-column'>
      <span className='bold'>{title}</span>
      <span className='paragraph-small zocks-text-gray-600'>{description}</span>
    </div>
    <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-700'>
      {option ? (
        <>
          <span className='bold'>{option.label}</span>
          {option.description ? (
            <>
              <span>·</span>
              <span>{option.description}</span>
            </>
          ) : null}
        </>
      ) : null}
      <InfoTooltip title='Set by your account admins.' label={`About ${title.toLowerCase()}`} />
    </span>
  </SettingsRow>
)

export const BookingRulesSection: FC<SectionProps> = ({ userSettings, accountSettings, isSaving = false, onUpdate, onReset }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { templates } = useSchedulingEmailTemplates()
  const bookingRules = userSettings.bookingRules
  const followUp = bookingRules?.followUp
  const sendReminder = followUp?.sendReminder ?? false
  const reminderDays = (followUp?.reminderOffsetMinutes ?? DEFAULT_REMINDER_MINUTES) / MINUTES_PER_DAY
  const [stopAfter, setStopAfter] = useState(4)

  const { data: shareUser } = useGetUserByIdQuery({ accountId, userId: authenticatedUser.id })
  const { data: schedulingShares } = useGetAllUserShareSettingsQuery({ accountId, userId: authenticatedUser.id, shareType: 'scheduling' })
  const schedulingSharingEnabled = shareUser?.data?.effectiveSettings?.schedulingSharingEnabled ?? false
  const sharedCount = schedulingShares?.data?.length ?? 0
  const sharingStatus = schedulingSharingEnabled ? (sharedCount > 0 ? `On · Shared with ${sharedCount} user${sharedCount === 1 ? '' : 's'}` : 'On') : 'Off'

  const accountBookingApproval = accountSettings?.approval?.booking
  const accountRescheduleApproval = accountSettings?.approval?.reschedule
  const accountAutomaticInvites = accountSettings?.automations?.automaticInvites
  const bookingEnforced = accountBookingApproval != null && accountBookingApproval !== ApprovalMode.USERS_DECIDE
  const rescheduleEnforced = accountRescheduleApproval != null && accountRescheduleApproval !== ApprovalMode.USERS_DECIDE
  const invitesEnforced = accountAutomaticInvites != null && accountAutomaticInvites !== AutomaticInvitesMode.USERS_DECIDE

  return (
    <SettingsSection title='Booking rules' subtitle='How bookings are handled once they come in.' isSaving={isSaving}>
      <SettingsGroup title='Approvals'>
        {bookingEnforced ? (
          <EnforcedValueRow
            title='Booking approval'
            description='Set whether bookings need approval before confirmation emails go to clients.'
            option={bookingApprovalOptions.find((option) => option.value === approvalValue(accountBookingApproval))}
          />
        ) : (
          <SettingsRow>
            <LabeledRadioCards
              title='Booking approval'
              description='Set whether bookings need approval before confirmation emails go to clients.'
              name='Booking approval'
              options={bookingApprovalOptions}
              value={approvalValue(bookingRules?.approval?.booking)}
              onChange={(booking) => onUpdate({ bookingRules: { ...bookingRules, approval: { ...bookingRules?.approval, booking } } })}
            />
          </SettingsRow>
        )}
        {rescheduleEnforced ? (
          <EnforcedValueRow
            title='Reschedule approval'
            description='Set whether reschedule requests from clients need approval before the new time is confirmed.'
            option={rescheduleApprovalOptions.find((option) => option.value === approvalValue(accountRescheduleApproval))}
          />
        ) : (
          <SettingsRow>
            <LabeledRadioCards
              title='Reschedule approval'
              description='Set whether reschedule requests from clients need approval before the new time is confirmed.'
              name='Reschedule approval'
              options={rescheduleApprovalOptions}
              value={approvalValue(bookingRules?.approval?.reschedule)}
              onChange={(reschedule) => onUpdate({ bookingRules: { ...bookingRules, approval: { ...bookingRules?.approval, reschedule } } })}
            />
          </SettingsRow>
        )}
      </SettingsGroup>
      <SettingsGroup title='Automations'>
        {invitesEnforced ? (
          <EnforcedValueRow
            title='Automatic invites after meetings'
            description="Set whether Future Meetings sends a calendar invite automatically when a meeting has a set time and you're available according to your calendar. Meetings without an invite wait under Opportunities in Scheduling."
            option={automaticInviteOptions.find((option) => option.value === inviteValue(accountAutomaticInvites))}
          />
        ) : (
          <SettingsRow>
            <LabeledRadioCards
              title='Automatic invites after meetings'
              description="Set whether Future Meetings sends a calendar invite automatically when a meeting has a set time and you're available according to your calendar. Meetings without an invite wait under Opportunities in Scheduling."
              name='Automatic invites after meetings'
              options={automaticInviteOptions}
              value={inviteValue(bookingRules?.automations?.automaticInvites)}
              onChange={(automaticInvites) => onUpdate({ bookingRules: { ...bookingRules, automations: { ...bookingRules?.automations, automaticInvites } } })}
            />
          </SettingsRow>
        )}
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Send follow-up reminder</span>
            <span className='paragraph-small zocks-text-gray-600'>Remind clients who haven&apos;t responded to a booking invitation.</span>
          </div>
          <Form.Switch
            aria-label='Send follow-up reminder'
            checked={sendReminder}
            onChange={(event) => onUpdate({ bookingRules: { ...bookingRules, followUp: { ...followUp, sendReminder: event.currentTarget.checked } } })}
          />
        </SettingsRow>
        <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-chevron-right zocks-text-gray-500' />
            <div className='d-flex flex-column'>
              <label className='bold' htmlFor='reminder-after'>
                Reminder after
              </label>
              <span className='paragraph-small zocks-text-gray-600'>Time to wait before the first reminder.</span>
            </div>
          </div>
          <Form.Select
            id='reminder-after'
            className='w-auto'
            disabled={!sendReminder}
            value={reminderDays}
            onChange={(event) =>
              onUpdate({
                bookingRules: { ...bookingRules, followUp: { ...followUp, reminderOffsetMinutes: Number(event.currentTarget.value) * MINUTES_PER_DAY } },
              })
            }
          >
            {REMINDER_DAY_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days} {days === 1 ? 'day' : 'days'} without a response
              </option>
            ))}
          </Form.Select>
        </SettingsRow>
        <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-chevron-right zocks-text-gray-500' />
            <div className='d-flex flex-column'>
              <label className='bold' htmlFor='follow-up-template'>
                Email template
              </label>
              <span className='paragraph-small zocks-text-gray-600'>Template used for every reminder.</span>
            </div>
          </div>
          <Form.Select
            id='follow-up-template'
            className='w-auto'
            disabled={!sendReminder}
            value={followUp?.reminderEmailTemplateId ?? ''}
            onChange={(event) => onUpdate({ bookingRules: { ...bookingRules, followUp: { ...followUp, reminderEmailTemplateId: event.currentTarget.value } } })}
          >
            <option value=''>Select a template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </Form.Select>
        </SettingsRow>
        <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-chevron-right zocks-text-gray-500' />
            <div className='d-flex flex-column'>
              <label className='bold' htmlFor='stop-reminders-after'>
                Stop reminders after
              </label>
              <span className='paragraph-small zocks-text-gray-600'>Total reminders to send before stopping.</span>
            </div>
          </div>
          <Form.Select
            id='stop-reminders-after'
            className='w-auto'
            disabled={!sendReminder}
            value={stopAfter}
            onChange={(event) => {
              setStopAfter(Number(event.currentTarget.value))
              dispatch(addToast({ variant: 'info', title: 'Not saved yet', messages: ['This limit will be configurable here soon.'] }))
            }}
          >
            {STOP_REMINDER_OPTIONS.map((count) => (
              <option key={count} value={count}>
                {count} reminders
              </option>
            ))}
          </Form.Select>
        </SettingsRow>
      </SettingsGroup>
      <SettingsGroup title='Collaboration'>
        <SettingsRow className='d-flex flex-wrap align-items-center justify-content-between gap-3 zocks-bg-color-gray-100'>
          <div className='d-flex flex-column'>
            <span className='bold'>Share bookings with my teammates</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Sets who you share your bookings with. Manage it in your <NavLink to='/account/profile-settings/collaboration'>Collaboration settings</NavLink>.
            </span>
          </div>
          <span className='paragraph-small zocks-text-gray-700'>{sharingStatus}</span>
        </SettingsRow>
      </SettingsGroup>
      <ResetToAccountDefaults
        title='Reset to account defaults'
        subtitle="Replace your booking rule settings with the account defaults. You'll lose any changes you've made here."
        confirmBody="This replaces your booking rule settings with the account defaults. Any changes you've made here will be lost."
        onReset={onReset}
      />
    </SettingsSection>
  )
}
